/**
 * Organization Settings Page
 *
 * Manage organization details and feature settings.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useOrganization } from '../../../context/OrganizationContext';
import { useAuth } from '../../../context/AuthContext';
import {
  getOrganization,
  updateOrganization,
  Organization,
  UpdateOrganizationInput,
} from '../../../api/organizations';
import { getErrorMessage } from '../../../api/client';
import { centsToDollars, parseDollars } from '../../../utils/rateFormatting';
import { Toggle } from '../../../components/ui/Toggle';
import { ToggleRow } from '../../../components/ui/ToggleRow';

export default function OrganizationSettings() {
  const { currentOrg, isOrgOwner } = useOrganization();
  const { refreshUser } = useAuth();

  // State
  const [, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<UpdateOrganizationInput>({
    name: '',
    description: '',
    contactEmail: '',
    contactPhone: '',
    paymentTrackingEnabled: false,
    declineSetsUnavailable: false,
    timezone: 'America/New_York',
  });

  // Default new-crew rates, held as dollar strings for editing (API is cents)
  const [dayRateInput, setDayRateInput] = useState('');
  const [hourlyRateInput, setHourlyRateInput] = useState('');

  // Fetch organization details
  const fetchOrganization = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getOrganization();
      setOrganization(response.organization);
      setFormData({
        name: response.organization.name,
        description: response.organization.description || '',
        contactEmail: response.organization.contactEmail || '',
        contactPhone: response.organization.contactPhone || '',
        paymentTrackingEnabled: response.organization.paymentTrackingEnabled,
        declineSetsUnavailable: response.organization.declineSetsUnavailable ?? false,
        timezone: response.organization.timezone || 'America/New_York',
      });
      setDayRateInput(centsToDollars(response.organization.defaultCrewDailyRate));
      setHourlyRateInput(centsToDollars(response.organization.defaultCrewHourlyRate));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleToggleChange = (name: string, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const response = await updateOrganization({
        ...formData,
        // Empty inputs mean "not set" — send null so the column is cleared
        // rather than '' (which is not a valid email/phone).
        contactEmail: formData.contactEmail?.trim() || null,
        contactPhone: formData.contactPhone?.trim() || null,
        defaultCrewDailyRate: parseDollars(dayRateInput),
        defaultCrewHourlyRate: parseDollars(hourlyRateInput),
      });
      setOrganization(response.organization);
      // Refresh user data to update org context with new settings (e.g., paymentTrackingEnabled)
      await refreshUser();
      toast.success('Organization settings saved');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
        <p className="mt-2 text-gray-600">Loading organization settings...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/admin/settings" className="hover:text-gray-700">
              Settings
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="w-4 h-4" />
          </li>
          <li className="text-gray-900 font-medium">Organization</li>
        </ol>
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Organization Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage settings for {currentOrg?.orgName || 'your organization'}
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Owner-only notice */}
      {!isOrgOwner && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex">
            <InfoIcon className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-yellow-700">
              <p className="font-medium">View Only</p>
              <p className="mt-1">
                Only organization owners can modify these settings.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* General Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            General Information
          </h2>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Organization Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!isOrgOwner}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                disabled={!isOrgOwner}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Brief description of your organization"
              />
            </div>

            {/* Contact Email */}
            <div>
              <label
                htmlFor="contactEmail"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Contact Email
              </label>
              <input
                type="email"
                id="contactEmail"
                name="contactEmail"
                value={formData.contactEmail || ''}
                onChange={handleInputChange}
                disabled={!isOrgOwner}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="contact@example.com"
              />
            </div>

            {/* Contact Phone */}
            <div>
              <label
                htmlFor="contactPhone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Contact Phone
              </label>
              <input
                type="tel"
                id="contactPhone"
                name="contactPhone"
                value={formData.contactPhone || ''}
                onChange={handleInputChange}
                disabled={!isOrgOwner}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Timezone */}
            <div>
              <label
                htmlFor="timezone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Timezone
              </label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone || 'America/New_York'}
                onChange={handleInputChange}
                disabled={!isOrgOwner}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Used for times in crew notification emails
              </p>
            </div>
          </div>
        </div>

        {/* Payment Tracking */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Tracking</h2>

          {/* Payment Tracking Toggle */}
          <div className="flex items-start justify-between py-4 border-b border-gray-100">
            <div className="flex-1 pr-4">
              <div className="flex items-center">
                <DollarIcon className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-900">
                  Enable Payment Tracking
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Enable tracking of crew rates, hours worked, and payment calculations.
                Turn this off for volunteer-based organizations.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Toggle
                enabled={formData.paymentTrackingEnabled || false}
                onChange={(value) =>
                  handleToggleChange('paymentTrackingEnabled', value)
                }
                disabled={!isOrgOwner}
                label="Enable Payment Tracking"
              />
            </div>
          </div>

          {/* Default rates for new crew members — grayed out until the
              feature is on. Values are kept (not cleared) while disabled. */}
          <div
            className={`pt-4 space-y-4 ${
              formData.paymentTrackingEnabled ? '' : 'opacity-50'
            }`}
          >
            <div>
              <label
                htmlFor="dayRate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Automatically set Day Rate for new crew members
              </label>
              <div className="relative w-48">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  type="number"
                  id="dayRate"
                  min="0"
                  step="0.01"
                  value={dayRateInput}
                  onChange={(e) => setDayRateInput(e.target.value)}
                  disabled={!isOrgOwner || !formData.paymentTrackingEnabled}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                New crew members start with this flat/day rate. Leave blank for no default.
              </p>
            </div>

            <div>
              <label
                htmlFor="hourlyRate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Automatically set hourly rate for new crew members
              </label>
              <div className="relative w-48">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  type="number"
                  id="hourlyRate"
                  min="0"
                  step="0.01"
                  value={hourlyRateInput}
                  onChange={(e) => setHourlyRateInput(e.target.value)}
                  disabled={!isOrgOwner || !formData.paymentTrackingEnabled}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                New crew members start with this hourly rate. Leave blank for no default.
                Applies only to members added after it's set; existing crew are unchanged.
              </p>
            </div>
          </div>
        </div>

        {/* Availability */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Availability</h2>
          <ToggleRow
            title="Mark crew unavailable when they decline"
            description="When a crew member declines an assignment, their availability for that event is set to Not available. When off, declining does not change their availability."
            enabled={formData.declineSetsUnavailable || false}
            onChange={(value) => handleToggleChange('declineSetsUnavailable', value)}
            disabled={!isOrgOwner}
            last
          />
        </div>

        {/* Save Button */}
        {isOrgOwner && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-r-transparent mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function DollarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
